import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { AvailableLanguages } from '@constants';
import { ConsumableCountry } from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { ReplaySubject } from 'rxjs';

export interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-nationality-select',
  templateUrl: './nationality-select.component.html',
  styleUrls: ['./nationality-select.component.scss'],
})
export class NationalitySelectComponent implements OnInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() fieldControl: FormControl;
  @Input() fieldKey: string;
  @Input() fieldLabelKey: string;
  @Input() isRequired: boolean;
  // Array of pseudoId -> you can find those in api -> modules -> common -> countries.ts
  @Input() filterOutIds?: number[];

  public nationalitiesList: SelectOption[];
  public currentLang: string;
  private rawCountries: ConsumableCountry[];

  constructor(
    private readonly commonsFacade: CommonsFacade,
    private readonly translateService: TranslateService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.currentLang = this.translateService.currentLang;
    this.rawCountries = await this.commonsFacade
      .getCountries$()
      .pipe(
        take(1),
        map(countries => {
          if (this.filterOutIds?.length) {
            return countries.filter(country => !this.filterOutIds.includes(country.id));
          } else {
            return countries;
          }
        })
      )
      .toPromise();

    this.translateService.onLangChange
      .pipe(startWith({ lang: this.currentLang }), takeUntil(this.destroy$))
      .subscribe(({ lang: langKey }) => {
        const langKeyTyped = langKey as AvailableLanguages;
        this.nationalitiesList = this.rawCountries.map(consumableCountry => ({
          value: consumableCountry.nationality,
          label: consumableCountry.nationalityTranslations[langKeyTyped],
        }));
        this.currentLang = langKey;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
